/**
 * Mock data + helpers cho luồng Auth (EG-356, EG-366, EG-367) khi BE chưa
 * có endpoint thật. Wrapped qua `withMockFallback` trong authService.
 */

export interface IdentifyTenant {
  /** ObjectId-shaped string */
  tenantId: string;
  /** Subdomain code (vd: kim-hoan) */
  tenantCode: string;
  /** Tên hiển thị tiệm vàng */
  name: string;
  /** Vai trò user trong tenant này */
  role: 'owner' | 'manager' | 'cashier' | 'inventory' | 'viewer';
  /** Plan tier để show badge khi chọn */
  planTier?: 'lite' | 'starter' | 'professional' | 'enterprise';
  /** Số store user có quyền truy cập trong tenant này */
  storeCount?: number;
}

export interface IdentifyResponse {
  /** True khi email tồn tại trong ít nhất 1 tenant */
  userExists: boolean;
  /** False khi user mới register OTP nhưng chưa set-password — FE switch sang recovery OTP. */
  hasPassword: boolean;
  tenants: IdentifyTenant[];
}

/** Mock 2 tenant cho email demo `owner@erpgold.vn`. */
export const mockTenantsForEmail: Record<string, IdentifyTenant[]> = {
  'owner@erpgold.vn': [
    {
      tenantId: 'tnt-001',
      tenantCode: 'kim-hoan',
      name: 'Tiệm Vàng Kim Hoàn',
      role: 'owner',
      planTier: 'professional',
      storeCount: 3,
    },
    {
      tenantId: 'tnt-002',
      tenantCode: 'phu-quy',
      name: 'Tiệm Vàng Phú Quý',
      role: 'manager',
      planTier: 'starter',
      storeCount: 1,
    },
  ],
  'manager@erpgold.vn': [
    {
      tenantId: 'tnt-001',
      tenantCode: 'kim-hoan',
      name: 'Tiệm Vàng Kim Hoàn',
      role: 'manager',
      planTier: 'professional',
      storeCount: 3,
    },
  ],
};

export function mockIdentify(email: string): IdentifyResponse {
  const normalized = email.trim().toLowerCase();
  const tenants = mockTenantsForEmail[normalized] ?? [];
  // Default: any non-empty email shows the demo tenant so dev/QA can proceed.
  if (!tenants.length && normalized) {
    return {
      userExists: true,
      hasPassword: true,
      tenants: [
        {
          tenantId: 'tnt-demo',
          tenantCode: 'demo',
          name: 'Tiệm Vàng Demo',
          role: 'owner',
          planTier: 'enterprise',
          storeCount: 1,
        },
      ],
    };
  }
  return { userExists: tenants.length > 0, hasPassword: tenants.length > 0, tenants };
}

export interface MockStore {
  storeId: string;
  storeCode: string;
  name: string;
  address?: string;
}

export const mockStoresForUser: MockStore[] = [
  { storeId: 'str-001', storeCode: 'CN01', name: 'Chi nhánh Quận 1', address: '123 Lê Lợi, Q.1' },
  { storeId: 'str-002', storeCode: 'CN05', name: 'Chi nhánh Quận 5', address: '45 Trần Hưng Đạo, Q.5' },
  { storeId: 'str-003', storeCode: 'CN-TD', name: 'Chi nhánh Thủ Đức', address: '789 Võ Văn Ngân' },
];

export function mockExchangeStoreToken(storeId: string) {
  return {
    access_token: `mock-access-${storeId}-${Date.now()}`,
    refresh_token: `mock-refresh-${storeId}-${Date.now()}`,
  };
}
